import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { formatApiMessage } from '@/api/client';
import { type TeamMember, usersApi } from '@/api/users.api';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { useAuthStore } from '@/stores/auth.store';
import { cn } from '@/lib/cn';
import type { UserRole } from '@/types/auth';

export function AdminUsersPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const [tab, setTab] = useState<'members' | 'pending'>('pending');

  const { data: members = [] } = useQuery({
    queryKey: ['users', 'list'],
    queryFn: usersApi.list,
  });

  const { data: pending = [] } = useQuery({
    queryKey: ['users', 'pending'],
    queryFn: usersApi.listPending,
  });

  const approve = useMutation({
    mutationFn: ({ uuid, role }: { uuid: string; role: 'admin' | 'manager' | 'member' | 'viewer' }) =>
      usersApi.approve(uuid, role),
    onSuccess: () => {
      toast.success(t('admin.user_approved'));
      qc.invalidateQueries({ queryKey: ['users', 'pending'] });
      qc.invalidateQueries({ queryKey: ['users', 'list'] });
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const reject = useMutation({
    mutationFn: (uuid: string) => usersApi.reject(uuid),
    onSuccess: () => {
      toast.success(t('admin.user_rejected'));
      qc.invalidateQueries({ queryKey: ['users', 'pending'] });
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const changeRole = useMutation({
    mutationFn: ({ uuid, role }: { uuid: string; role: UserRole }) => usersApi.changeRole(uuid, role),
    onSuccess: () => {
      toast.success(t('admin.role_changed'));
      qc.invalidateQueries({ queryKey: ['users', 'list'] });
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const remove = useMutation({
    mutationFn: (uuid: string) => usersApi.remove(uuid),
    onSuccess: () => {
      toast.success(t('admin.user_removed'));
      qc.invalidateQueries({ queryKey: ['users', 'list'] });
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  return (
    <AdminLayout title={t('admin.users_title')} subtitle={t('admin.users_subtitle')}>
      <nav className="tabs mb-4">
        <button
          className={`tab ${tab === 'pending' ? 'tab-active' : ''}`}
          onClick={() => setTab('pending')}
        >
          {t('admin.pending_tab')}
          {pending.length > 0 && (
            <span className="badge badge-warning ml-1.5">{pending.length}</span>
          )}
        </button>
        <button
          className={`tab ${tab === 'members' ? 'tab-active' : ''}`}
          onClick={() => setTab('members')}
        >
          {t('admin.members_tab')}
          <span className="badge badge-muted ml-1.5">{members.length}</span>
        </button>
      </nav>

      {tab === 'pending' && (
        <div className="card !p-0">
          {pending.length === 0 ? (
            <div className="text-center py-12 text-muted text-[13px]">
              {t('admin.no_pending')}
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>{t('admin.th_user')}</th>
                  <th>{t('admin.th_registered')}</th>
                  <th className="text-right">{t('admin.th_action')}</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((u) => (
                  <PendingRow
                    key={u.id}
                    user={u}
                    onApprove={(role) => approve.mutate({ uuid: u.id, role })}
                    onReject={() => {
                      if (confirm(t('admin.reject_confirm', { name: u.firstName }))) {
                        reject.mutate(u.id);
                      }
                    }}
                    loading={approve.isPending || reject.isPending}
                    t={t}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'members' && (
        <div className="card !p-0">
          <table className="table">
            <thead>
              <tr>
                <th>{t('admin.th_user')}</th>
                <th>{t('admin.th_role')}</th>
                <th>{t('admin.th_status')}</th>
                <th>{t('admin.th_last_active')}</th>
                <th className="text-right">{t('admin.th_action')}</th>
              </tr>
            </thead>
            <tbody>
              {members.map((u) => (
                <MemberRow
                  key={u.id}
                  user={u}
                  isMe={u.id === me?.id}
                  meRole={me?.role ?? 'viewer'}
                  onChangeRole={(role) => changeRole.mutate({ uuid: u.id, role })}
                  onRemove={() => {
                    if (confirm(t('admin.remove_confirm', { name: u.firstName }))) {
                      remove.mutate(u.id);
                    }
                  }}
                  t={t}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

function PendingRow({
  user,
  onApprove,
  onReject,
  loading,
  t,
}: {
  user: TeamMember;
  onApprove: (role: 'admin' | 'manager' | 'member' | 'viewer') => void;
  onReject: () => void;
  loading: boolean;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const [role, setRole] = useState<'admin' | 'manager' | 'member' | 'viewer'>('member');
  return (
    <tr>
      <td>
        <div className="font-medium text-gray-900">
          {user.firstName} {user.lastName}
        </div>
        <div className="text-[12px] text-muted">{user.email}</div>
      </td>
      <td className="text-[12px] text-muted">
        {new Date(user.createdAt).toLocaleDateString('az-AZ', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })}
      </td>
      <td className="text-right">
        <div className="flex justify-end gap-2 items-center">
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as 'admin' | 'manager' | 'member' | 'viewer')}
            className="text-[12px] border border-border rounded-md px-2 py-1 bg-white"
          >
            <option value="viewer">{t('admin.role_viewer')}</option>
            <option value="member">{t('admin.role_member')}</option>
            <option value="manager">{t('admin.role_manager')}</option>
            <option value="admin">{t('admin.role_admin')}</option>
          </select>
          <button
            onClick={() => onApprove(role)}
            disabled={loading}
            className="btn btn-primary btn-sm"
          >
            {t('admin.approve')}
          </button>
          <button
            onClick={onReject}
            disabled={loading}
            className="btn btn-ghost btn-sm text-danger"
          >
            {t('admin.reject')}
          </button>
        </div>
      </td>
    </tr>
  );
}

function MemberRow({
  user,
  isMe,
  meRole,
  onChangeRole,
  onRemove,
  t,
}: {
  user: TeamMember;
  isMe: boolean;
  meRole: UserRole;
  onChangeRole: (role: UserRole) => void;
  onRemove: () => void;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  // Can't edit yourself or accounts at or above your level (owner is sacred).
  const canEdit = !isMe && meRole === 'owner';
  return (
    <tr>
      <td>
        <div className="font-medium text-gray-900">
          {user.firstName} {user.lastName} {isMe && <span className="text-[10px] text-muted">({t('admin.you')})</span>}
        </div>
        <div className="text-[12px] text-muted">{user.email}</div>
      </td>
      <td>
        {canEdit && user.role !== 'owner' ? (
          <select
            value={user.role}
            onChange={(e) => onChangeRole(e.target.value as UserRole)}
            className="text-[12px] border border-border rounded-md px-2 py-1 bg-white"
          >
            <option value="viewer">{t('admin.role_viewer')}</option>
            <option value="member">{t('admin.role_member')}</option>
            <option value="manager">{t('admin.role_manager')}</option>
            <option value="admin">{t('admin.role_admin')}</option>
          </select>
        ) : (
          <span className={cn('badge text-[10px]', user.role === 'owner' && 'badge-primary')}>
            {t(`admin.role_${user.role}`)}
          </span>
        )}
      </td>
      <td>
        <span className={cn(
          'badge text-[10px]',
          user.status === 'active' ? 'badge-success' : 'badge-muted',
        )}>
          {t(`settings_team.status_${user.status}`)}
        </span>
      </td>
      <td className="text-[12px] text-muted">
        {user.lastLoginAt
          ? new Date(user.lastLoginAt).toLocaleDateString('az-AZ')
          : '—'}
      </td>
      <td className="text-right">
        {canEdit && user.role !== 'owner' && (
          <button onClick={onRemove} className="btn btn-ghost btn-sm text-danger">
            {t('admin.remove')}
          </button>
        )}
      </td>
    </tr>
  );
}
